import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Outlet, Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { formatDistanceToNow } from "date-fns";
import { CHAT_CONFIG, resolveUser } from "@/lib/chat";
import { RefreshCcw, Loader2 } from "lucide-react";

export default function ChatList() {
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("all");
    const [resolvedUsers, setResolvedUsers] = useState({});
    const [chats, setChats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { astrologer } = useAuth();
    const { userId: selectedUserId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Check if we're on mobile and if a specific chat is selected
    const isMobile = window.innerWidth < 768;
    const isChatSelected = selectedUserId && location.pathname.includes('/chat/');
    const showChatList = !isMobile || !isChatSelected;
    const showChatContent = !isMobile || isChatSelected;

    // Fetch chats from CX3
    const fetchChats = useCallback(async () => {
        if (!astrologer?.astrologerId || !astrologer?.accessToken) {
            setIsLoading(false);
            return;
        }

        try {
            setError(null);
            
            // Get token
            const tokenResponse = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/chat/token/astrologer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${astrologer.accessToken}`,
                },
            });

            if (!tokenResponse.ok) {
                throw new Error('Failed to get chat token');
            }

            const tokenData = await tokenResponse.json();
            const token = tokenData.data?.token;

            if (!token) {
                throw new Error('No token received');
            }

            // Fetch chats from CX3
            const chatsResponse = await fetch(`${CHAT_CONFIG.serverUrl}/api/v1/secured/chats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!chatsResponse.ok) {
                throw new Error(`Failed to fetch chats: ${chatsResponse.status}`);
            }

            const chatsData = await chatsResponse.json();
            
            if (chatsData.success && chatsData.chats) {
                const userId = `astro_${astrologer.astrologerId}`;
                const chatList = chatsData.chats.map(chat => {
                    const otherParticipant = chat.participants.find(p => p !== userId) || chat.participants[0] || 'Unknown';
                    return {
                        id: chat.id,
                        participants: chat.participants,
                        lastMessageAt: chat.last_message_at ? new Date(chat.last_message_at) : null,
                        lastMessageContent: chat.last_message_content || '',
                        messageCount: chat.message_count,
                        unreadCount: 0, // TODO: fetch unread counts
                        otherParticipant,
                    };
                });

                // Sort by last message time
                chatList.sort((a, b) => {
                    if (!a.lastMessageAt) return 1;
                    if (!b.lastMessageAt) return -1;
                    return b.lastMessageAt.getTime() - a.lastMessageAt.getTime();
                });

                setChats(chatList);
            } else {
                setChats([]);
            }
        } catch (err) {
            console.error('[ChatList] Error fetching chats:', err);
            setError(err.message || 'Failed to load chats');
        } finally {
            setIsLoading(false);
        }
    }, [astrologer?.astrologerId, astrologer?.accessToken]);

    // Initial fetch and polling
    useEffect(() => {
        fetchChats();
        const interval = setInterval(fetchChats, 15000);
        return () => clearInterval(interval);
    }, [fetchChats]);

    // Resolve user metadata for display names
    useEffect(() => {
        const resolveUserMetadata = async () => {
            const newResolved = { ...resolvedUsers };
            let hasNew = false;

            for (const chat of chats) {
                if (!newResolved[chat.otherParticipant]) {
                    try {
                        const metadata = await resolveUser(chat.otherParticipant);
                        newResolved[chat.otherParticipant] = metadata;
                        hasNew = true;
                    } catch (e) {
                        console.error('Failed to resolve user:', chat.otherParticipant, e);
                        newResolved[chat.otherParticipant] = { displayName: chat.otherParticipant };
                    }
                }
            }

            if (hasNew) {
                setResolvedUsers(newResolved);
            }
        };

        if (chats.length > 0) {
            resolveUserMetadata();
        }
    }, [chats]);

    // Transform chats to expected format
    const chatList = useMemo(() => {
        return chats.map(chat => {
            // Extract user ID from participant (e.g., "user_123" -> "123")
            const participantId = chat.otherParticipant.replace('user_', '');
            const resolved = resolvedUsers[chat.otherParticipant] || {};

            return {
                userId: participantId,
                name: resolved.displayName || chat.otherParticipant,
                lastMessage: chat.lastMessageContent || 'No messages yet',
                lastMessageTimestamp: chat.lastMessageAt,
                unreadCount: chat.unreadCount || 0,
                avatar: resolved.avatar,
                isOnline: false,
            };
        });
    }, [chats, resolvedUsers]);

    // Filter chat list based on active tab and search term
    const filteredChatList = useMemo(() => {
        let filtered = chatList;

        if (activeTab === "unread") {
            filtered = filtered.filter(chat => chat.unreadCount > 0);
        }

        if (searchTerm) {
            filtered = filtered.filter(chat =>
                chat.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [chatList, searchTerm, activeTab]);

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
    }, []);

    const handleChatClick = useCallback((chatUserId) => {
        if (isMobile) {
            navigate(`/interact/chat/${chatUserId}`);
        }
    }, [isMobile, navigate]);

    const renderChatItem = useCallback((chat) => (
        <Link
            key={chat.userId}
            to={`/interact/chat/${chat.userId}`}
            onClick={() => handleChatClick(chat.userId)}
            className={`flex items-center p-4 hover:bg-gray-100 ${selectedUserId === chat.userId ? "bg-orange-50" : "bg-white"
                } my-2 rounded-3xl shadow-sm border border-gray-100`}
        >
            <div className="relative">
                {chat.avatar ? (
                    <img
                        src={chat.avatar}
                        alt={chat.name}
                        className="w-14 h-14 rounded-full object-cover"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div
                    className={`w-14 h-14 rounded-full bg-orange-400 items-center justify-center text-white text-3xl font-semibold ${chat.avatar ? 'hidden' : 'flex'}`}
                >
                    {chat.name ? chat.name.charAt(0).toUpperCase() : "?"}
                </div>
                <div className={`absolute bottom-0 right-0 w-4 h-4 ${chat.isOnline ? 'bg-green-500' : 'bg-gray-400'} rounded-full border-2 border-white`}></div>
            </div>
            <div className="ml-4 flex-1">
                <div className="flex justify-between items-center">
                    <h3 className="font-medium text-base">{chat.name}</h3>
                    <span className="text-xs text-gray-400">
                        {chat.lastMessageTimestamp && !isNaN(new Date(chat.lastMessageTimestamp).getTime()) ? formatDistanceToNow(new Date(chat.lastMessageTimestamp), { addSuffix: true }) : ''}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-400 truncate mt-1 flex-1">
                        {chat.lastMessage || "No messages yet"}
                    </p>
                    {chat.unreadCount > 0 && (
                        <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                            {chat.unreadCount}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    ), [selectedUserId, handleChatClick]);

    // Show loading state
    if (!astrologer?.astrologerId) {
        return (
            <div className="flex h-[calc(100vh-125px)] items-center justify-center">
                <div className="text-center p-8 text-gray-500">
                    Please log in to view messages
                </div>
            </div>
        );
    }

    // Show error state
    if (error && !isLoading) {
        return (
            <div className="flex h-[calc(100vh-125px)] items-center justify-center">
                <div className="text-center p-8">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button
                        onClick={fetchChats}
                        className="px-4 py-2 bg-orange-500 text-white rounded-full"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-125px)]">
            {showChatList && (
                <div className="w-full md:w-1/3 border-r overflow-y-auto">
                    <div className="p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">Messages</h2>
                            <button
                                onClick={fetchChats}
                                disabled={isLoading}
                                className={`p-1 rounded-full hover:bg-gray-100 transition-colors`}
                            >
                                <RefreshCcw
                                    className={`h-4 w-4 ${isLoading ? 'animate-spin text-orange-500' : 'text-gray-600'}`}
                                />
                            </button>
                        </div>

                        <div className="relative mb-4">
                            <input
                                type="text"
                                placeholder="Search by name"
                                className="w-full p-3 pl-12 border border-gray-200 rounded-full text-base bg-gray-100 text-gray-500 focus:outline-none"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <svg
                                className="absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>

                        <div className="flex space-x-4 mb-4">
                            <button
                                onClick={() => handleTabChange("all")}
                                className={`px-6 py-3 rounded-full font-medium ${activeTab === "all"
                                    ? "bg-orange-500 text-white"
                                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => handleTabChange("unread")}
                                className={`px-6 py-3 rounded-full font-medium ${activeTab === "unread"
                                    ? "bg-orange-500 text-white"
                                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                    }`}
                            >
                                Unread
                            </button>
                        </div>
                    </div>

                    <div className="px-4 pb-4">
                        {isLoading && chatList.length === 0 ? (
                            <div className="flex justify-center items-center p-8">
                                <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
                            </div>
                        ) : filteredChatList.length > 0 ? (
                            filteredChatList.map(renderChatItem)
                        ) : (
                            <div className="text-center p-8 text-gray-500">
                                {searchTerm
                                    ? "No matching conversations found"
                                    : activeTab === "unread"
                                        ? "No unread messages"
                                        : "No conversations yet"
                                }
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showChatContent && (
                <div className="w-full md:w-2/3">
                    <Outlet />
                </div>
            )}
        </div>
    );
}
